<script>
import { mapState } from 'vuex'
import NodeCardBox from '@/components/node/NodeCardBox.vue'
import { ready } from '@jsplumb/browser-ui'
import { getInputEndpointUUID, getOutputEndpointUUID } from '@/utils/scenario-view.utils'

export default {
    name: 'NodeStep',
    mounted () {
        ready(() => {
            this.$nextTick(async () => {
                if (this.node.children) {
                    this.addConnections()
                }
            })
        })
    },
    components: { NodeCardBox },
    props: {
        node: null
    },
    computed: {
        ...mapState('debug', ['instance']),
        suggestionConfig () {
            if (this.node && this.node.type === 'suggestion' && this.node.config.config) {
                return JSON.parse(this.node.config.config)
            }
            return {}
        }
    },
    methods: {
        change (val) {
            if (val.length <= 0) {
                this.hiddenConnection()
            } else {
                setTimeout(() => {
                    this.showConnection()
                }, 350)
            }
            this.$emit('repaint')
        },
        addConnections () {
            this.node.children.forEach((node, idx) => {
                const nextNode = this.node.children[idx + 1]
                if (nextNode) {
                    const inputUuid = getInputEndpointUUID(nextNode.id, idx + 1)
                    const outputUuid = getOutputEndpointUUID(node.id, idx)
                    this.instance.connect({
                        uuids: [outputUuid, inputUuid],
                        overlays: [
                            { type: 'Arrow', options: { width: 12, length: 7, location: 1, id: 'arrow' } }
                        ],
                        type: 'fci-transparent'
                    })
                }
            })
        },
        hiddenConnection () {
            const connections = this.instance.select({ scope: this.node.id })
            connections.entries.forEach((connection) => {
                connection.setType('fci-transparent')
            })
        },
        showConnection () {
            const connections = this.instance.select({ scope: this.node.id })
            connections.entries.forEach((connection) => {
                connection.setType('fci-normal')
            })
            this.instance.repaintEverything()
        }
    }
}
</script>

<template>
    <el-collapse @change="change" style="" :class="{error: node.error && node.type === 'suggestion' }">
    <el-collapse-item>
            <template slot="title">
                <div class="d-flex align-center" style="gap: 12px" >
                    <div class="step-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
                            <g clip-path="url(#clip0_1_33044)">
                                <path d="M14 2C14 2.44425 13.8069 2.84339 13.5 3.11805V5H18.5C20.1569 5 21.5 6.34315 21.5 8V18C21.5 19.6569 20.1569 21 18.5 21H6.5C4.84315 21 3.5 19.6569 3.5 18V8C3.5 6.34315 4.84315 5 6.5 5H11.5V3.11805C11.1931 2.84339 11 2.44425 11 2C11 1.17157 11.6716 0.5 12.5 0.5C13.3284 0.5 14 1.17157 14 2ZM6.5 7C5.94772 7 5.5 7.44772 5.5 8V18C5.5 18.5523 5.94772 19 6.5 19H18.5C19.0523 19 19.5 18.5523 19.5 18V8C19.5 7.44772 19.0523 7 18.5 7H13.5H11.5H6.5ZM2.5 10H0.5V16H2.5V10ZM22.5 10H24.5V16H22.5V10ZM9.5 14.5C10.3284 14.5 11 13.8284 11 13C11 12.1716 10.3284 11.5 9.5 11.5C8.67157 11.5 8 12.1716 8 13C8 13.8284 8.67157 14.5 9.5 14.5ZM15.5 14.5C16.3284 14.5 17 13.8284 17 13C17 12.1716 16.3284 11.5 15.5 11.5C14.6716 11.5 14 12.1716 14 13C14 13.8284 14.6716 14.5 15.5 14.5Z" fill="white"/>
                            </g>
                            <defs>
                                <clipPath id="clip0_1_33044">
                                    <rect width="24" height="24" fill="white" transform="translate(0.5)"/>
                                </clipPath>
                            </defs>
                        </svg>
                    </div>
                    <span class="font-weight-bold">{{node.type | capitalize}}:</span>
                    <el-tooltip class="item" :content="node.config.name" placement="top-start">
                        <span class="step-name">{{node.config.name}}</span>
                    </el-tooltip>
                </div>
            </template>
            <div v-if="node.type === 'suggestion'" class="d-flex" style="flex-direction: column; gap: 8px">
                <div class="d-flex">
                    <span class="font-weight-bold" style="flex-basis: 20%">Header:</span>
                    <span style="flex-basis: 80%">{{ suggestionConfig.header }}</span>
                </div>
                <div class="d-flex">
                    <span class="font-weight-bold" style="flex-basis: 20%">Label 1:</span>
                    <span style="flex-basis: 80%">{{ suggestionConfig.suggest_1 }}</span>
                </div>
                <div class="d-flex">
                    <span class="font-weight-bold" style="flex-basis: 20%">Label 2:</span>
                    <span style="flex-basis: 80%">{{ suggestionConfig.suggest_2 }}</span>
                </div>
                <div class="d-flex">
                    <span class="font-weight-bold" style="flex-basis: 20%">Label 3:</span>
                    <span style="flex-basis: 80%">{{ suggestionConfig.suggest_3 }}</span>
                </div>
                <div class="d-flex">
                    <span class="font-weight-bold" style="flex-basis: 20%">Footer:</span>
                    <span style="flex-basis: 80%">{{ suggestionConfig.footer }}</span>
                </div>
                <div class="d-flex" v-if="node.error">
                    <span class="font-weight-bold" style="flex-basis: 20%">Error:</span>
                    <span style="flex-basis: 80%">{{ node.error }}</span>
                </div>
            </div>
            <div v-else>
                <div v-if="node.children" class="d-flex flex-column align-center" style="gap: 30px">
                    <NodeCardBox v-for="(card, idx) of node.children" :key="idx" :node="card" :index="idx" :scope="node.id"/>
                </div>
                <div style="color: #7B7B7B" v-else>
                    {{$t('noDataAvailable') | capitalize}}
                </div>
            </div>
        </el-collapse-item>
    </el-collapse>
</template>

<style scoped lang="scss">
.step-icon {
    border-radius: 4px;
    background: #716FFF;
    display: flex;
    padding: 4px;
}
.step-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
}
</style>
